import type { FunctionComponent } from 'preact';
import { useState } from 'preact/hooks';
import { t } from '../../../domain/localisation';

// Keyed by descriptor name; the default field set yields
// `{ given_names, last_name, email?, phone_number? }`.
export type UserDetails = Record<string, string>;

export interface FieldOption {
  // JSON config can carry numeric codes (e.g. `44`); compared as strings.
  value: string | number;
  label: string;
}

/**
 * A single field the form should render, describing name, type, whether it is
 * required, an optional validation `regex`, and an optional `oneOfGroup` (all
 * members of a group are individually optional but the group needs ≥1 valid
 * value). A `select` field renders a native dropdown of `options`. This
 * mirrors the `FieldDescriptor` shape the embed KYC flows build from the v3
 * services config (`services-config.js`), so a product can drive this screen
 * straight from the server response.
 */
export interface FieldDescriptor {
  name: string;
  label?: string;
  type?: 'text' | 'email' | 'phone' | 'select';
  required?: boolean;
  regex?: string;
  oneOfGroup?: string;
  placeholder?: string;
  options?: FieldOption[];
}

interface UserDetailsViewProps {
  themeColor: string;
  hideAttribution: boolean;
  fields?: FieldDescriptor[] | null;
  initialValues?: Record<string, string> | null;
  // Whole-form read-only (demo mode): every input locked, validation skipped,
  // Continue always enabled.
  readOnly?: boolean;
  // Per-field lock (partial partner prefill): these fields render read-only
  // while the rest stay editable and gate Continue as usual.
  lockedFields?: string[] | null;
  onSubmit: (details: Record<string, string>) => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// E.164: a leading '+', then 7-15 digits with no leading zero. Formatting
// characters (spaces/hyphens/parens) are stripped before this test.
const E164_PATTERN = /^\+[1-9]\d{6,14}$/;

// Strip formatting characters, then treat a leading `00` international
// prefix as an alias for `+` (how many users outside North America dial),
// so `002348000000000` normalizes to `+2348000000000`.
const normalizePhone = (value: string) =>
  value.replace(/[\s\-()]/g, '').replace(/^00/, '+');

// The four fields this screen has always collected. Rendered when the caller
// passes no `fields` — keeps the standalone SmartSelfie/DocV mounts (which
// mount the element with no descriptors) rendering exactly as before. An empty
// `fields` list means nothing is left to collect, not "use the defaults".
const DEFAULT_FIELDS: FieldDescriptor[] = [
  { name: 'given_names', type: 'text', required: true },
  { name: 'last_name', type: 'text', required: true },
  { name: 'email', type: 'email', required: false, oneOfGroup: 'contact' },
  {
    name: 'phone_number',
    type: 'phone',
    required: false,
    oneOfGroup: 'contact',
    regex: E164_PATTERN.source,
  },
];

// i18n + autofill metadata for the fields this screen knows by name, so the
// visible copy matches the design ("First name", "Email (optional)") regardless
// of the server-supplied label. Unknown fields (id_number, operator, …) fall
// back to their server `label` and generic validation messages.
const FIELD_META: Record<
  string,
  {
    label: string;
    required?: string;
    invalid?: string;
    placeholder?: string;
    autocomplete?: string;
  }
> = {
  given_names: {
    label: 'userDetails.givenNames.label',
    required: 'userDetails.givenNames.required',
    autocomplete: 'given-name',
  },
  last_name: {
    label: 'userDetails.lastName.label',
    required: 'userDetails.lastName.required',
    autocomplete: 'family-name',
  },
  email: {
    label: 'userDetails.email.label',
    invalid: 'userDetails.email.invalid',
    autocomplete: 'email',
  },
  phone_number: {
    label: 'userDetails.phoneNumber.label',
    invalid: 'userDetails.phoneNumber.invalid',
    placeholder: 'userDetails.phoneNumber.placeholder',
    autocomplete: 'tel',
  },
};

const interpolate = (key: string, field: string) =>
  t(key).replace(/\{\{field\}\}/g, field);

const compileRegex = (regex?: string): RegExp | null => {
  if (!regex) return null;
  try {
    return new RegExp(regex);
  } catch {
    // A malformed server pattern degrades to "no format check" rather than
    // throwing out of rendering.
    return null;
  }
};

// Stable DOM id for a field: `given_names` → `given-names`, keeping the ids the
// standalone element has always exposed (`#given-names`, `#phone-number`, …).
const fieldId = (name: string) => name.replace(/_/g, '-');

const labelFor = (descriptor: FieldDescriptor): string => {
  const meta = FIELD_META[descriptor.name];
  let base = meta ? t(meta.label) : descriptor.label || descriptor.name;
  // Group members are individually optional — mark them so, unless the resolved
  // label already carries an "(optional)"-style parenthetical.
  if (descriptor.oneOfGroup && !/\(/.test(base)) {
    base = `${base} (${t('userDetails.optional')})`;
  }
  return base;
};

const inputTypeFor = (descriptor: FieldDescriptor): string => {
  if (descriptor.type === 'email') return 'email';
  if (descriptor.type === 'phone') return 'tel';
  return 'text';
};

const toText = (value: unknown) => (value == null ? '' : String(value));

const hasOption = (descriptor: FieldDescriptor, value: string) =>
  (descriptor.options || []).some(
    (o) => toText(o.value).trim() === value.trim(),
  );

// Whether a single field's current value is acceptable on its own: required
// fields must be non-empty; any non-empty value must match its format (regex,
// or the built-in email/phone rule for those types). A select value must be one
// of its options, so a stale prefill can't slip past the dropdown.
const isFieldValid = (descriptor: FieldDescriptor, rawValue: string) => {
  const value = (rawValue || '').trim();
  if (value.length === 0) return !descriptor.required;
  if (descriptor.type === 'select') {
    return hasOption(descriptor, value);
  }
  const regex = compileRegex(descriptor.regex);
  if (descriptor.type === 'phone') {
    return (regex || E164_PATTERN).test(normalizePhone(value));
  }
  if (descriptor.type === 'email') {
    return (regex || EMAIL_PATTERN).test(value);
  }
  return regex ? regex.test(value) : true;
};

// A group member "counts" toward its group when it is non-empty AND valid.
const memberSatisfiesGroup = (
  descriptor: FieldDescriptor,
  rawValue: string,
) => {
  const value = (rawValue || '').trim();
  return value.length > 0 && isFieldValid(descriptor, value);
};

/**
 * Shared, product-agnostic in-flow "user details" form. Rendered only when the
 * partner config omits `user_details`. With no `fields` prop it collects the
 * default first/last name + contact set; a caller can instead pass descriptors
 * built from the v3 services config to render an arbitrary, server-driven field
 * set (e.g. Biometric KYC's id_number + names + contact).
 */
export const UserDetailsView: FunctionComponent<UserDetailsViewProps> = ({
  themeColor,
  hideAttribution,
  fields,
  initialValues,
  readOnly = false,
  lockedFields,
  onSubmit,
}) => {
  const descriptors = Array.isArray(fields) ? fields : DEFAULT_FIELDS;
  const locked = new Set(Array.isArray(lockedFields) ? lockedFields : []);

  // Only the user's own edits are held in state; the displayed value is the edit
  // when present, otherwise the seeded `initialValues`. This keeps prefill
  // reactive (it can arrive after mount — e.g. when a host sets the `values`
  // property/attribute in a separate tick) without a lazy `useState` seed
  // silently missing it, and without an effect that would clobber typing.
  const seed = initialValues || {};
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // A native select can only show one of its options, so a seed outside them
  // would sit in state unseen: submitted invisibly in read-only mode, and
  // never valid (nor fixable) when locked. Treat it as empty instead, and
  // leave that select editable, so the user sees the placeholder and picks.
  const isStaleSelectSeed = (d: FieldDescriptor) => {
    const seeded = toText(seed[d.name]).trim();
    return d.type === 'select' && seeded.length > 0 && !hasOption(d, seeded);
  };

  const valueOf = (d: FieldDescriptor) => {
    if (d.name in edits) return edits[d.name];
    return isStaleSelectSeed(d) ? '' : toText(seed[d.name]);
  };

  const isLocked = (d: FieldDescriptor) =>
    readOnly || (locked.has(d.name) && !isStaleSelectSeed(d));

  const setField = (name: string) => (e: Event) => {
    const { value } = e.target as HTMLInputElement | HTMLSelectElement;
    setEdits((prev) => ({ ...prev, [name]: value }));
  };

  const setTouchedField = (name: string) => () => {
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  // Group the descriptors by `oneOfGroup` so we can evaluate the "provide at
  // least one" rule and render one shared message per group.
  const groups: Record<string, FieldDescriptor[]> = {};
  descriptors.forEach((d) => {
    if (!d.oneOfGroup) return;
    (groups[d.oneOfGroup] = groups[d.oneOfGroup] || []).push(d);
  });

  const groupSatisfied = (members: FieldDescriptor[]) =>
    members.some((d) => memberSatisfiesGroup(d, valueOf(d)));

  const fieldValid = (d: FieldDescriptor) => isFieldValid(d, valueOf(d));

  // Continue is enabled once every non-group field is valid and every group is
  // satisfied. Read-only mode (demo / locked prefill) skips validation so the
  // pre-filled, non-editable form is never a dead-end.
  const allFieldsValid = descriptors
    .filter((d) => !d.oneOfGroup)
    .every((d) => fieldValid(d));
  const allGroupsSatisfied = Object.values(groups).every(groupSatisfied);
  const isValid = readOnly || (allFieldsValid && allGroupsSatisfied);

  // Surface a group's requirement proactively — either after the user has
  // touched a member, or once every other required field is valid — so the
  // greyed-out Continue is explained rather than a silent dead-end.
  const nonGroupRequiredValid = descriptors
    .filter((d) => d.required && !d.oneOfGroup)
    .every((d) => fieldValid(d));

  const handleSubmit = (e: Event) => {
    e.preventDefault();
    if (!isValid) return;
    const payload: Record<string, string> = {};
    descriptors.forEach((d) => {
      let value = valueOf(d).trim();
      if (value.length === 0) return; // omit blank optionals
      if (d.type === 'email') value = value.toLowerCase();
      if (d.type === 'phone') value = normalizePhone(value);
      payload[d.name] = value;
    });
    onSubmit(payload);
  };

  const renderField = (descriptor: FieldDescriptor, isLast: boolean) => {
    const { name } = descriptor;
    const id = fieldId(name);
    const meta = FIELD_META[name];
    const lockd = isLocked(descriptor);
    const valid = fieldValid(descriptor);
    const showError = !lockd && touched[name] && !valid;
    const errorId = `${id}-error`;
    const value = valueOf(descriptor).trim();

    let errorMessage = '';
    if (showError) {
      if (value.length === 0) {
        errorMessage = meta && meta.required ? t(meta.required) : '';
        if (!errorMessage) {
          errorMessage = interpolate(
            'userDetails.fieldRequired',
            labelFor(descriptor),
          );
        }
      } else {
        errorMessage =
          meta && meta.invalid
            ? t(meta.invalid)
            : interpolate('userDetails.invalidFormat', labelFor(descriptor));
      }
    }

    const placeholder =
      meta && meta.placeholder ? t(meta.placeholder) : descriptor.placeholder;

    const a11y = {
      ...(descriptor.required ? { 'aria-required': 'true' as const } : {}),
      'aria-describedby': showError ? errorId : undefined,
      'aria-invalid': showError,
    };

    if (descriptor.type === 'select') {
      return (
        <div className="field" key={name}>
          <label htmlFor={id}>{labelFor(descriptor)}</label>
          <div className="select-wrap">
            <select
              id={id}
              name={name}
              value={valueOf(descriptor)}
              disabled={lockd}
              onChange={setField(name)}
              onBlur={setTouchedField(name)}
              {...a11y}
            >
              <option value="">
                {placeholder || t('userDetails.selectPlaceholder')}
              </option>
              {(descriptor.options || []).map((option) => (
                <option key={toText(option.value)} value={toText(option.value)}>
                  {option.label}
                </option>
              ))}
            </select>
            <svg
              className="chevron"
              aria-hidden="true"
              width="12"
              height="8"
              viewBox="0 0 12 8"
            >
              <path
                d="M1 1.5 6 6.5 11 1.5"
                fill="none"
                stroke="#5E646E"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          {showError && (
            <span id={errorId} className="error" role="alert">
              {errorMessage}
            </span>
          )}
        </div>
      );
    }

    return (
      <div className="field" key={name}>
        <label htmlFor={id}>{labelFor(descriptor)}</label>
        <input
          id={id}
          name={name}
          type={inputTypeFor(descriptor)}
          {...(descriptor.type === 'email' ? { inputMode: 'email' } : {})}
          {...(meta && meta.autocomplete
            ? { autocomplete: meta.autocomplete }
            : {})}
          enterkeyhint={isLast ? 'done' : 'next'}
          {...(placeholder ? { placeholder } : {})}
          value={valueOf(descriptor)}
          readOnly={lockd}
          onInput={setField(name)}
          onBlur={setTouchedField(name)}
          {...a11y}
        />
        {showError && (
          <span id={errorId} className="error" role="alert">
            {errorMessage}
          </span>
        )}
      </div>
    );
  };

  const renderGroupMessages = () => {
    if (readOnly) return null;
    return Object.entries(groups).map(([groupName, members]) => {
      if (groupSatisfied(members)) return null;
      const anyProvided = members.some((d) => valueOf(d).trim().length > 0);
      const anyTouched = members.some((d) => touched[d.name]);
      const show = !anyProvided && (anyTouched || nonGroupRequiredValid);
      if (!show) return null;
      // 'contact' keeps its dedicated class so existing tests/styles that look
      // for `.contact-required` still match; other groups reuse the styling.
      return (
        <span key={groupName} className="error contact-required" role="alert">
          {t('userDetails.contactRequired')}
        </span>
      );
    });
  };

  return (
    <form className="user-details" onSubmit={handleSubmit}>
      <h1 className="title">{t('userDetails.title')}</h1>
      <p className="body">{t('userDetails.body')}</p>

      {descriptors.map((descriptor, index) =>
        renderField(descriptor, index === descriptors.length - 1),
      )}

      {renderGroupMessages()}

      <div className="actions">
        <button
          type="submit"
          className="continue"
          style={{ background: themeColor, borderColor: themeColor }}
          disabled={!isValid}
        >
          {t('userDetails.continue')}
        </button>
      </div>

      {!hideAttribution && (
        // @ts-expect-error preact-custom-element types
        <powered-by-smile-id />
      )}

      <style>{`
        :host { display: block; height: 100%; }
        .user-details {
          display: flex;
          flex-direction: column;
          min-height: 100%;
          padding: clamp(0.75rem, 2.5dvh, 1.5rem) clamp(1rem, 4vw, 1.25rem) clamp(0.5rem, 1.5dvh, 0.5rem);
          font-family: "DM Sans", system-ui, sans-serif;
          background: #F5F7FA;
          color: #1A1A1A;
          box-sizing: border-box;
        }
        .user-details > powered-by-smile-id {
          display: block;
          width: 100%;
          margin-top: auto;
          padding-top: clamp(0.5rem, 1.5dvh, 1rem);
          background: #F5F7FA;
          flex-shrink: 0;
        }
        .title {
          font-family: "DM Sans", system-ui, sans-serif;
          font-size: clamp(15px, 2.2dvh, 18px);
          font-weight: 600;
          line-height: 1.25;
          text-align: center;
          color: #21232C;
          margin: clamp(0.5rem, 1.5dvh, 1.25rem) 0 clamp(0.5rem, 1.2dvh, 1rem);
          flex-shrink: 0;
        }
        .body {
          color: #5E646E;
          text-align: center;
          font-size: clamp(11px, 1.5dvh, 12px);
          font-weight: 400;
          line-height: 1.35;
          margin: 0 0 clamp(0.75rem, 2dvh, 1.5rem);
          flex-shrink: 0;
        }
        .field {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          margin-bottom: clamp(0.6rem, 1.5dvh, 0.9rem);
          flex-shrink: 0;
        }
        .field label {
          font-size: clamp(11px, 1.5dvh, 12px);
          font-weight: 600;
          color: #21232C;
        }
        .field input,
        .field select {
          height: clamp(38px, 5.5dvh, 44px);
          padding: 0 0.85rem;
          border-radius: 10px;
          border: 1px solid #D5DAE1;
          background: #FFF;
          font-family: inherit;
          font-size: clamp(13px, 1.8dvh, 14px);
          color: #21232C;
          box-sizing: border-box;
        }
        .field input::placeholder {
          color: #94A3B8;
        }
        /* The chevron shares the select's grid cell and sits at its inline end
           via justify-self, which follows the inherited direction on iOS 13+
           (:dir() and inset-inline-end are newer). */
        .select-wrap {
          display: grid;
          grid-template-columns: minmax(0, 1fr);
          align-items: center;
        }
        .select-wrap > select,
        .select-wrap > .chevron {
          grid-area: 1 / 1;
        }
        .select-wrap > .chevron {
          justify-self: end;
          margin: 0 0.85rem;
          pointer-events: none;
        }
        .field select {
          appearance: none;
          -webkit-appearance: none;
          padding-inline-end: 2.25rem;
        }
        .field input:focus,
        .field select:focus {
          outline: none;
          border-color: #94A3B8;
        }
        .field input[aria-invalid="true"],
        .field select[aria-invalid="true"] {
          border-color: #DC2626;
        }
        .field input[readonly],
        .field select:disabled {
          background-color: #EEF1F5;
          color: #5E646E;
          cursor: not-allowed;
          opacity: 1;
        }
        .error {
          font-size: 11px;
          color: #DC2626;
          line-height: 1.3;
        }
        .contact-required {
          display: block;
          margin: -0.25rem 0 0.75rem;
        }
        .actions {
          display: flex;
          flex-direction: column;
          gap: clamp(0.4rem, 1dvh, 0.75rem);
          margin-top: auto;
          padding-top: clamp(0.5rem, 1.5dvh, 1.5rem);
          flex-shrink: 0;
        }
        .continue {
          width: 100%;
          height: clamp(40px, 6dvh, 48px);
          padding: 0 1rem;
          border-radius: 24px;
          border: 1px solid;
          color: white;
          font-size: clamp(0.95rem, 1.8dvh, 1.05rem);
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
        }
        .continue:disabled { opacity: 0.45; cursor: not-allowed; }
      `}</style>
    </form>
  );
};

export default UserDetailsView;
